import {
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useFormik } from 'formik';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { get } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown } from 'react-feather';
import * as Yup from 'yup';

//Components
import FlagCountry from 'src/components/common/FlagCountry';
import FormikForm from 'src/components/common/FormikForm';
import IconFeather from 'src/components/common/Icon';
import TooltipFormLabel from 'src/components/common/TooltipFormLabel';
import FormItemCompanyName from 'src/components/Form/FormItemCompanyName';
import FormItemConstantSetting from 'src/components/Form/FormItemConstantSetting';
import ButtonBack from 'src/features/Onboarding/ButtonBack';
import ButtonContinue from 'src/features/Onboarding/ButtonContinue';
import { TYPE_STEP_INFORMATION } from 'src/features/Onboarding/config';
import PopupSelectPhoneCode from 'src/features/Setting/PopupSelectPhoneCode';
import { useMutateCheckWorkEmail } from 'src/services/mutate';
import { CONSTANT_SETTING_TYPES } from 'src/utils/constants/enum';
import { SUCCESS_CODE } from 'src/utils/constants/statusCode';
import { getCountryByPhoneCode } from 'src/utils/helpers/stringHelpers';
import {
  validateEmail,
  validateEmailBlocked,
  validateURL,
  validateWebsiteDomainValid,
} from 'src/utils/helpers/validate';
import { useOnboardingStore } from 'src/zustand/useOnboardingStore';

const StepInformationCompany = (props: IFormOnboardingProps) => {
  const theme = useTheme();
  const { dispatch, informations } = useOnboardingStore((state) => state);
  const { mutateAsync, isPending: isPendingCheckWorkEmail } = useMutateCheckWorkEmail();

  const [visiblePhoneCode, setVisiblePhoneCode] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const formik = useFormik({
    initialValues: {
      [TYPE_STEP_INFORMATION.company.company_name]: informations?.company_name,
      [TYPE_STEP_INFORMATION.company.department]: informations?.department,
      [TYPE_STEP_INFORMATION.company.website_address]: informations?.website_address,
      [TYPE_STEP_INFORMATION.company.role]: informations?.role,
      [TYPE_STEP_INFORMATION.company.work_email]: informations?.work_email || '',
      [TYPE_STEP_INFORMATION.company.industry]: informations?.industry,
      [TYPE_STEP_INFORMATION.company.confirm_work_email]: informations?.confirm_work_email || false,
      [TYPE_STEP_INFORMATION.company.phone_code]: informations?.phone_code || '',
      [TYPE_STEP_INFORMATION.company.phone_number]: informations?.phone_number || '',
    },
    validationSchema: Yup.object().shape({
      company_name: Yup.string().max(255).required('Government Agency Name is required'),
      department: Yup.string(),
      role: Yup.string(),
      industry: Yup.string().max(255),
      work_email: Yup.string()
        .max(255)
        .email('Email Invalid')
        .test('email', 'Email Invalid', (text?: string) => validateEmail(text || '')),
      website_address: Yup.string()
        .max(255)
        .test(
          'website_address',
          'Does not match website syntax',
          (text?: string) => validateURL(text || '') && validateWebsiteDomainValid(text || ''),
        ),
      confirm_work_email: Yup.bool().test(
        'email',
        'Please use your work-provided email address',
        (confirm?: boolean, context?: ExpectedAny) => {
          return Boolean(confirm) || validateEmailBlocked(context?.parent?.work_email);
        },
      ),
      phone_number: Yup.string()
        .max(255)
        .required('Phone number is required')
        .test('phone_code', 'Phone number is invalid', (text?: string, context?: ExpectedAny) => {
          const code = context?.parent?.phone_code;
          const phoneNumber = code + (text || '');
          if (!text) {
            return true;
          }
          return isValidPhoneNumber(phoneNumber || '');
        }),
      phone_code: Yup.string().max(255),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(false);
    },
    validateOnMount: true,
  });

  const countryCode = useMemo(() => {
    const country = getCountryByPhoneCode(String(formik.values.phone_code) || '');
    return country.code;
  }, [formik.values.phone_code]);

  const checkEmail = useCallback(async () => {
    if (formik.values.work_email && validateEmail(String(formik.values.work_email))) {
      const resCheckEmail = await mutateAsync({ work_email: String(formik.values.work_email) });
      if (resCheckEmail.status_code !== SUCCESS_CODE.GET) {
        formik.setFieldError('work_email', resCheckEmail.message);
        return;
      }
    }
  }, [formik.values.work_email]);

  const renderError = (type: string) => {
    return (
      get(formik.touched, type) &&
      get(formik.errors, type) && (
        <FormHelperText error id="standard-weight-helper-text-email-login">
          {get(formik.errors, type)}
        </FormHelperText>
      )
    );
  };

  const renderErrorWorkingEmail = () => {
    if (isPendingCheckWorkEmail) return;
    if (get(formik.errors, TYPE_STEP_INFORMATION.company.work_email)) {
      return renderError('work_email');
    }

    return (
      <Stack direction="row" alignItems="center" spacing={0.5}>
        {get(formik.errors, TYPE_STEP_INFORMATION.company.confirm_work_email) && (
          <FormHelperText error id="standard-weight-helper-text-email-login">
            {get(formik.errors, TYPE_STEP_INFORMATION.company.confirm_work_email)}
          </FormHelperText>
        )}
        {!validateEmailBlocked(String(formik.values.work_email)) && (
          <FormControlLabel
            sx={{ margin: 0 }}
            control={
              <Checkbox
                size="small"
                onChange={(e, value) => {
                  formik.setFieldValue('confirm_work_email', value);
                }}
                checked={Boolean(formik.values.confirm_work_email)}
                color="primary"
              />
            }
            label={<Typography variant="body2">I promise this is my work email</Typography>}
          />
        )}
      </Stack>
    );
  };

  const getError = (type: string) => {
    return get(formik.touched, type) && Boolean(get(formik.errors, type));
  };

  //Update Data form onboarding global state
  useEffect(() => {
    dispatch({
      informations: formik.values,
    });
  }, [formik.values]);

  return (
    <FormikForm formik={formik}>
      <Typography variant="h4">Tell us about yourself</Typography>

      <Box sx={{ mt: 4 }} />

      <Stack gap={2} direction={'column'}>
        <FormControl>
          <FormItemCompanyName
            id={TYPE_STEP_INFORMATION.company.company_name}
            required
            error={getError(TYPE_STEP_INFORMATION.company.company_name)}
            freeSolo={true}
          />
          {renderError(TYPE_STEP_INFORMATION.company.company_name)}
        </FormControl>
        <FormControl>
          <FormItemConstantSetting
            id={TYPE_STEP_INFORMATION.company.industry}
            error={getError(TYPE_STEP_INFORMATION.company.industry)}
            label={
              <>
                What Industry Are You In?
                <TooltipFormLabel title="Please select from the list, please choose as close as possible" />
              </>
            }
            type={CONSTANT_SETTING_TYPES.information_industry}
          />
          {renderError(TYPE_STEP_INFORMATION.company.industry)}
        </FormControl>
        <FormControl>
          <FormItemConstantSetting
            id={TYPE_STEP_INFORMATION.company.department}
            error={getError(TYPE_STEP_INFORMATION.company.department)}
            label={
              <>
                Department
                <TooltipFormLabel title="Please select from the list, please choose as close as possible" />
              </>
            }
            type={CONSTANT_SETTING_TYPES.information_department}
          />
          {renderError(TYPE_STEP_INFORMATION.company.department)}
        </FormControl>
        <FormControl>
          <FormItemConstantSetting
            id={TYPE_STEP_INFORMATION.company.role}
            error={getError(TYPE_STEP_INFORMATION.company.role)}
            label={
              <>
                Which function best describes your role?
                <TooltipFormLabel title="Please select from the list, please choose as close as possible" />
              </>
            }
            type={CONSTANT_SETTING_TYPES.information_role}
          />
          {renderError(TYPE_STEP_INFORMATION.company.role)}
        </FormControl>
        <FormControl>
          <TextField
            size="small"
            label={'Website Address'}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.website_address}
            id={TYPE_STEP_INFORMATION.company.website_address}
            error={getError(TYPE_STEP_INFORMATION.company.website_address)}
          />
          {renderError(TYPE_STEP_INFORMATION.company.website_address)}
        </FormControl>
        <FormControl>
          <TextField
            onBlur={formik.handleBlur}
            error={Boolean(getError('phone_number'))}
            id="phone_number"
            name="phone_number"
            value={formik.values.phone_number}
            onChange={formik.handleChange}
            required
            InputProps={{
              startAdornment: (
                <InputAdornment
                  sx={{
                    background: theme.palette.grey[100],
                    height: 'fit-content',
                    padding: '4px 8px',
                    borderRadius: theme.shape.borderRadius / 4,
                  }}
                  onClick={(e) => {
                    setVisiblePhoneCode(true);
                    setAnchorEl(e.currentTarget);
                  }}
                  component={Box}
                  position="start"
                >
                  <FlagCountry width={16} code={countryCode} />
                  <Typography sx={{ ml: 1 }} color={theme.palette.text.secondary} variant="body2">
                    {formik.values['phone_code']}
                  </Typography>
                  <IconFeather muiProps={{ fontSize: 'small', sx: { ml: 1 } }} icon={<ChevronDown />} />
                </InputAdornment>
              ),
            }}
            size="small"
            fullWidth
            label="Your Phone Number"
          />
          {renderError(TYPE_STEP_INFORMATION.company.phone_number)}
          <PopupSelectPhoneCode
            anchorEl={anchorEl}
            id="phone_code"
            open={visiblePhoneCode}
            handleClose={() => setVisiblePhoneCode(false)}
          />
        </FormControl>
        <FormControl>
          <TextField
            size="small"
            name={TYPE_STEP_INFORMATION.company.work_email}
            onChange={formik.handleChange}
            onBlur={(e) => {
              formik.handleBlur(e);
              checkEmail();
            }}
            value={formik.values.work_email}
            id={TYPE_STEP_INFORMATION.company.work_email}
            label={
              <>
                What Is Your Work Email Address? <TooltipFormLabel title="" />
              </>
            }
            InputProps={{
              endAdornment: isPendingCheckWorkEmail ? (
                <InputAdornment position="end">
                  <CircularProgress color="inherit" size={16} />
                </InputAdornment>
              ) : null,
            }}
            error={getError(TYPE_STEP_INFORMATION.company.work_email)}
          />
          {renderErrorWorkingEmail()}
        </FormControl>
      </Stack>

      <Box sx={{ mt: 4 }} />

      <Stack alignItems={'center'} direction={'row'} justifyContent={'end'} gap={3}>
        <ButtonBack onClick={props.onClickBack} />
        <ButtonContinue
          disabled={!formik.isValid}
          onClick={() => {
            if (formik.isValid) {
              props.onClickContinue();
            }
          }}
        />
      </Stack>
    </FormikForm>
  );
};

export default StepInformationCompany;
